import { call, put, takeLatest } from "redux-saga/effects";
import { dashboardLoaderAC } from "../system/systemActionCreators";
import {
  getZreportsList,
  exportZReportsList,
  exportZReportsSales,
} from "./dZreportsAPI";
import {
  typeZreportsFilters,
  IdZreportsStateDataItem,
} from "./dZreportReducer";
import {
  paginationMapper,
  typePagination,
  typeRequestPagination,
} from "../system/constants";
import {
  dZReportsSetDataAC,
  dZReportsSetDataSagaAC,
  dZReportsExportDataSagaAC,
  dZReportsExportSalesSagaAC,
  dZReportsSetPaginationAC,
} from "./dZreportsActionCreators";

import FileDownload from "js-file-download";
import { LANGUAGE } from "locales/languages";
import { errorGenerator, notificationCreator } from "model/system/utils";
import { getLanguage } from "model/axiosConfig";

// Temp data list mapper
const tempDataMapper = (tempResponseData: any): IdZreportsStateDataItem[] => {
  return tempResponseData.map((item: IdZreportsStateDataItem) => {
    return {
      checked: false,
      id: item.id,
      fiscalCardId: item.fiscalCardId,
      tin: item.tin,
      zreportNumber: item.zreportNumber,
      openDate: item.openDate,
      closeDate: item.closeDate,
      firstReceiptNumber: item.firstReceiptNumber,
      lastReceiptNumber: item.lastReceiptNumber,
      salesWithCash: item.salesWithCash,
      salesWithCards: item.salesWithCards,
      vatAmount: item.vatAmount,
      salesCount: item.salesCount,
      returnWithCash: item.returnWithCash,
      returnWithCards: item.returnWithCards,
      vatReturnAmount: item.vatReturnAmount,
      returnCount: item.returnCount,
      merchantName: item.merchantName,
      merchantAddress: item.merchantAddress,
      terminalSerianNumber: item.terminalSerianNumber,
      userId: item.userId,
      userName: item.userName,
      cashierId: item.cashierId,
      store: item.store,
      softwareVersion: item.softwareVersion,
    };
  });
};

// Get data function-generator
function* getData(payload: {
  filters: typeZreportsFilters;
  paginationRequest: typeRequestPagination;
}) {
  let response = yield call(getZreportsList, payload);

  if (response.status === 200) {
    const tempData: IdZreportsStateDataItem[] = tempDataMapper(
      response.data.content,
    );
    const pagination: typePagination = paginationMapper(response.data);

    yield put(dZReportsSetPaginationAC(pagination));

    yield put(dZReportsSetDataAC(tempData));
  } else {
    yield errorGenerator(response);
  }
}

// Set z-reports list saga
export function* dZReportsSetDataWorker(actionObject: {
  type: string;
  payload: {
    filters: typeZreportsFilters;
    paginationRequest: typeRequestPagination;
  };
}) {
  yield put(dashboardLoaderAC(true));

  yield getData(actionObject.payload);

  yield put(dashboardLoaderAC(false));
}

export function* dZReportsSetDataWatcher() {
  yield takeLatest(dZReportsSetDataSagaAC.type, dZReportsSetDataWorker);
}

// Export data saga
export function* dZReportsExportDataWorker(actionObject: {
  type: string;
  payload: { zReportsIds: string[] };
}) {
  yield put(dashboardLoaderAC(true));

  const lang = getLanguage();

  let response = yield call(exportZReportsList, {
    zReportsIds: actionObject.payload.zReportsIds,
    lang: lang,
  });

  if (response.status === 200) {
    if (response?.data?.exportType === "SYNC" && response?.data?.result) {
      const binaryString = atob(response?.data?.result);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);

      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const blob = new Blob([bytes], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      FileDownload(blob, "z-reports.xlsx");
    } else {
      yield notificationCreator("SUCCESS_EXPORT");
    }
  } else {
    yield errorGenerator(response);
  }

  yield put(dashboardLoaderAC(false));
}

export function* dZReportsExportDataWatcher() {
  yield takeLatest(dZReportsExportDataSagaAC.type, dZReportsExportDataWorker);
}

// Export data saga
export function* dZReportsExportSalesWorker(actionObject: {
  type: string;
  payload: { zReportsIds: string[]; lang: LANGUAGE };
}) {
  const langSwitcher = {
    ru: "RUS",
    en: "ENG",
    uz: "UZB",
  };

  yield put(dashboardLoaderAC(true));

  // @ts-ignore
  let response = yield call(exportZReportsSales, {
    zReportsIds: actionObject.payload.zReportsIds,
    lang: langSwitcher[actionObject.payload.lang],
  });

  if (response.status === 200) {
    if (response?.data?.exportType === "SYNC" && response?.data?.result) {
      const binaryString = atob(response?.data?.result);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);

      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const blob = new Blob([bytes], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      FileDownload(blob, "z-reports-sales.xlsx");
    } else {
      yield notificationCreator("SUCCESS_EXPORT");
    }
  } else {
    yield errorGenerator(response);
  }

  yield put(dashboardLoaderAC(false));
}

export function* dZReportsExportSalesWatcher() {
  yield takeLatest(dZReportsExportSalesSagaAC.type, dZReportsExportSalesWorker);
}
